'use strict'

// Agent assignments — org-scoped, SERVER-BACKED relationships between
// agents (a User with kind:'agent') and people (a User). This is the
// canonical "agent X is assigned to person Y" model, replacing the
// per-user canvas connection-graph for agent↔person edges. Mirrors
// functions/teams.js: loaded once per session through the shared SDK into
// root state, with optimistic local updates on write.
//
//   root.agentAssignmentsByAssignee  { [assigneeUserId]: [{ id, agent, assignee, role, status, orgId }] }
//   root.agentAssignmentsByAgent     { [agentUserId]:    [{ id, agent, assignee, role, status, orgId }] }
//
// IDs here are Mongo User _ids. The canvas + the "Your agents" row map
// between these and their node ids (agent username slug / person userId)
// via the helpers below + root._agents (the merged roster, whose entries
// carry { id: slug, userId: mongoId }).

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
import { shellResolveClaims } from './claimResolver.js'

// Same list-shape unwrap teams.js uses — servers return {items} / {data} /
// {data:{items}} / bare array depending on endpoint + version.
const _shellUnwrapList = (r) => r?.items || r?.data?.items || r?.data || (Array.isArray(r) ? r : [])

const _shellGetSdk = () => {
  if (typeof window === 'undefined') return null
  return shellGetSDK()
}

// A populated ref may be an object ({_id,name,...}) or a bare id string.
const _idOf = (ref) => {
  if (!ref) return null
  if (typeof ref === 'string') return ref
  return ref._id || ref.id || null
}

// The active workspace org — used for assignment writes.
export const agentAssignOrgId = (root) =>
  root?.jwtClaims?.workspace_org_id ||
  (Array.isArray(root?.jwtClaims?.orgs) && root.jwtClaims.orgs[0]?.id) ||
  root?.workspace?.id ||
  null

// agent node slug -> agent User _id (resolved from the merged roster).
export const agentUserIdForSlug = (root, slug) => {
  const a = (Array.isArray(root?._agents) ? root._agents : []).find(
    (x) => String(x.id).toLowerCase() === String(slug || '').toLowerCase()
  )
  return a?.userId || null
}

// agent User _id -> roster entry (for name/caption on render).
export const agentBySlugUserId = (root, userId) =>
  (Array.isArray(root?._agents) ? root._agents : []).find(
    (x) => String(x.userId) === String(userId)
  ) || null

// ─── Viewer-scoped visibility ─────────────────────────────────────
// Shared with the /my-agents canvas (team-and-agents.js keeps a mirror
// for its own package — canvas can't import workspace functions except
// via el.call, so the tier logic is intentionally duplicated across the
// package boundary; keep the two in sync). The home "Your agents" widget
// and /my-agents must agree on what each viewer sees:
//   admin   — owner|co-owner|admin org role, admin.teams perm, or jwt
//             is_owner → every agent.
//   manager — leads ≥1 team (team-maintainer in teamsByEmail) → the
//             agents assigned to self + the people on teams they lead.
//   member  — agents assigned to self.
const _ADMIN_ROLES = ['owner', 'co-owner', 'admin', 'superadmin']

export const viewerTier = (root) => {
  // Gate-A claim cutover: is_owner / permissions sourced from the Mongo
  // active-org row (claimResolver.js). Kept in lock-step with the canvas
  // `_viewerTier` mirror.
  const claims = shellResolveClaims(root)
  const role = ((root?.roles && root.roles.byUser) || {})[root?.currentUserId] || null
  const perms = Array.isArray(claims.permissions) ? claims.permissions : []
  if (claims.is_owner || _ADMIN_ROLES.includes(String(role || '')) || perms.includes('admin.teams'))
    return 'admin'
  // id-first: a non-mgmt viewer's own roster row carries no email, so the
  // email map is empty for them — resolve their teams via the stable userId
  // (root.currentUserId is the Mongo _id) before falling back to email
  // (tickets/fable.md P0 regression).
  const uid = root?.currentUserId != null ? String(root.currentUserId) : ''
  const email = String(root?.currentUserEmail || '').toLowerCase()
  const byId = (uid && root?.teamsByUserId?.[uid]) || null
  const myTeams = (byId && byId.length ? byId : email && root?.teamsByEmail?.[email]) || []
  if (myTeams.some((t) => /maintainer|manager|lead/i.test(String(t.role || '')))) return 'manager'
  return 'member'
}

// People (User _ids) on teams the viewer leads; [] for non-managers.
const _shellPeopleUnderViewer = (root) => {
  // id-first membership resolution (non-mgmt rows carry no email) — see
  // viewerTier / tickets/fable.md P0 regression.
  const uid = root?.currentUserId != null ? String(root.currentUserId) : ''
  const email = String(root?.currentUserEmail || '').toLowerCase()
  const byIdMap = root?.teamsByUserId || {}
  const byEmail = root?.teamsByEmail || {}
  const myById = (uid && byIdMap[uid]) || null
  const myTeams = (myById && myById.length ? myById : email && byEmail[email]) || []
  const leadIds = myTeams
    .filter((t) => /maintainer|manager|lead/i.test(String(t.role || '')))
    .map((t) => String(t.id))
  if (!leadIds.length) return []
  const out = []
  for (const p of Array.isArray(root?.people) ? root.people : []) {
    if (!p?.userId) continue
    if (
      (uid && String(p.userId) === uid) ||
      (email && String(p?.email || '').toLowerCase() === email)
    )
      continue
    const rows =
      (p.userId != null && byIdMap[String(p.userId)]) ||
      byEmail[String(p?.email || '').toLowerCase()] ||
      []
    if (rows.some((t) => leadIds.includes(String(t.id)))) out.push(p.userId)
  }
  return out
}

// The agents the current viewer should see, enriched with assignment
// metadata (so the card can render the relationship badge). Admin → the
// full roster; manager → agents assigned to self + reports; member →
// agents assigned to self. Deduped by agent id.
export const agentsVisibleToViewer = (root) => {
  const tier = viewerTier(root)
  if (tier === 'admin') {
    const roster = Array.isArray(root?._agents) ? root._agents : []
    return roster.map((a) => ({ ...a }))
  }
  const assigneeIds = [root?.currentUserId, ..._shellPeopleUnderViewer(root)].filter(Boolean)
  const seen = new Set()
  const out = []
  for (const uid of assigneeIds) {
    for (const a of agentsAssignedTo(root, uid)) {
      if (seen.has(a.id)) continue
      seen.add(a.id)
      out.push(a)
    }
  }
  return out
}

// Load assignments across every org the user belongs to, indexed both
// ways. Feature-detected: a no-op (empty maps) until sdk.listAgentAssignments
// ships in the SDK dist, so the UI degrades gracefully meanwhile.
// smbls reconciles object-valued state by key, so a key that was present
// in a prior map but is absent after a rebuild/unassign can linger as
// `undefined` rather than being dropped — and the `{ ...prev }` spreads in
// the optimistic writers below then carry that `undefined` forward
// indefinitely. `_pruneMap` keeps only non-empty arrays so the maps stay
// clean (no `undefined`/empty values), self-healing any inherited cruft.
// `_commitMaps` is the single write point that prunes before every update.
// Pairs with the Array.isArray guard in agentsAssignedTo (belt + braces).
const _pruneMap = (m) => {
  const out = {}
  for (const k in m) if (Array.isArray(m[k]) && m[k].length) out[k] = m[k]
  return out
}
const _commitMaps = (root, byAssignee, byAgent) => {
  const a = _pruneMap(byAssignee)
  const g = _pruneMap(byAgent)
  root?.update?.({ agentAssignmentsByAssignee: a, agentAssignmentsByAgent: g })
  return { agentAssignmentsByAssignee: a, agentAssignmentsByAgent: g }
}

export const loadAllAgentAssignments = async (root) => {
  const sdk = await _shellGetSdk()
  if (!sdk || typeof sdk.listAgentAssignments !== 'function') {
    return { agentAssignmentsByAssignee: {}, agentAssignmentsByAgent: {} }
  }
  const orgs = root?.jwtClaims?.orgs || []
  const byAssignee = {}
  const byAgent = {}

  await Promise.all(
    orgs.map(async (org) => {
      const orgId = org.id
      if (!orgId) return
      const res = await sdk.listAgentAssignments(orgId).catch((err) => {
        const msg = (err && err.message) || ''
        // Membership-gated orgs throw — that's expected, stay quiet.
        if (!/membership|not.*member|forbidden|403|404/i.test(msg)) {
          console.warn('[agentAssignments] list failed for', orgId, msg)
        }
        return null
      })
      if (!res) return
      for (const a of _shellUnwrapList(res)) {
        // The server populates both the Mongo _id and the federated
        // supabaseUserId. The frontend's id space is mixed: agents +
        // currentUserId are Mongo _ids, but workspace people (canvas nodes,
        // person.userId) are supabaseUserId UUIDs. Use the UUID as the
        // canonical assignee id (so canvas edges + person rows match) and
        // dual-key the index under EVERY alias so any lookup space resolves.
        const agentMongo = _idOf(a.agent)
        const agentUuid = (a.agent && a.agent.supabaseUserId) || null
        const assigneeMongo = _idOf(a.assignee)
        const assigneeUuid = (a.assignee && a.assignee.supabaseUserId) || null
        const entry = {
          id: a._id || a.id,
          agent: agentMongo,
          agentName: (a.agent && (a.agent.name || a.agent.username)) || null,
          assignee: assigneeUuid || assigneeMongo,
          assigneeName: (a.assignee && (a.assignee.name || a.assignee.email)) || null,
          role: a.role || 'assignee',
          status: a.status || 'active',
          relationship: (a.metadata && a.metadata.relationship) || null,
          orgId
        }
        for (const k of new Set([assigneeUuid, assigneeMongo].filter(Boolean))) {
          ;(byAssignee[k] = byAssignee[k] || []).push(entry)
        }
        for (const k of new Set([agentMongo, agentUuid].filter(Boolean))) {
          ;(byAgent[k] = byAgent[k] || []).push(entry)
        }
      }
    })
  )

  return _commitMaps(root, byAssignee, byAgent)
}

// Create an assignment (agentUserId + assigneeUserId are Mongo User _ids).
// Optimistically merges into both indexes so the canvas + Your agents
// re-derive immediately.
export const assignAgentToUser = async (
  root,
  { orgId, agentUserId, assigneeUserId, role = 'assignee' }
) => {
  const sdk = await _shellGetSdk()
  if (!sdk || typeof sdk.assignAgent !== 'function')
    throw new Error('Agent assignment isn’t available on this build yet')
  if (!orgId || !agentUserId || !assigneeUserId)
    throw new Error('orgId, agentUserId and assigneeUserId are required')

  const res = await sdk.assignAgent(orgId, {
    agentId: agentUserId,
    assigneeId: assigneeUserId,
    role
  })
  const created = (res && res.data) || res || {}
  const entry = {
    id: created._id || created.id,
    agent: agentUserId,
    assignee: assigneeUserId,
    role: created.role || role,
    status: created.status || 'active',
    relationship: null,
    orgId
  }
  const byAssignee = { ...(root.agentAssignmentsByAssignee || {}) }
  const byAgent = { ...(root.agentAssignmentsByAgent || {}) }
  byAssignee[assigneeUserId] = [...(byAssignee[assigneeUserId] || []), entry]
  byAgent[agentUserId] = [...(byAgent[agentUserId] || []), entry]
  _commitMaps(root, byAssignee, byAgent)
  return entry
}

// Remove an assignment (optimistic).
export const unassignAgentFromUser = async (
  root,
  { orgId, assignmentId, agentUserId, assigneeUserId }
) => {
  const sdk = await _shellGetSdk()
  if (!sdk || typeof sdk.unassignAgent !== 'function')
    throw new Error('Agent assignment isn’t available on this build yet')
  await sdk.unassignAgent(orgId, assignmentId)
  const byAssignee = { ...(root.agentAssignmentsByAssignee || {}) }
  const byAgent = { ...(root.agentAssignmentsByAgent || {}) }
  if (assigneeUserId && byAssignee[assigneeUserId]) {
    byAssignee[assigneeUserId] = byAssignee[assigneeUserId].filter((a) => a.id !== assignmentId)
    if (!byAssignee[assigneeUserId].length) delete byAssignee[assigneeUserId]
  }
  if (agentUserId && byAgent[agentUserId]) {
    byAgent[agentUserId] = byAgent[agentUserId].filter((a) => a.id !== assignmentId)
    if (!byAgent[agentUserId].length) delete byAgent[agentUserId]
  }
  _commitMaps(root, byAssignee, byAgent)
}

// Set (or clear) the relationship label on an assignment ("reports-to",
// "assists", …). Stored in AgentAssignment.metadata.relationship via the
// existing updateAgentAssignment SDK method; optimistic local patch.
export const setAgentAssignmentRelationship = async (
  root,
  { orgId, assignmentId, agentUserId, assigneeUserId, relationship }
) => {
  const sdk = await _shellGetSdk()
  if (!sdk || typeof sdk.updateAgentAssignment !== 'function')
    throw new Error('Agent assignment update isn’t available on this build yet')
  if (!orgId || !assignmentId) throw new Error('orgId and assignmentId are required')
  const rel = relationship || null
  await sdk.updateAgentAssignment(orgId, assignmentId, { metadata: { relationship: rel } })
  const patch = (e) => (e.id === assignmentId ? { ...e, relationship: rel } : e)
  const byAssignee = { ...(root.agentAssignmentsByAssignee || {}) }
  const byAgent = { ...(root.agentAssignmentsByAgent || {}) }
  if (assigneeUserId && byAssignee[assigneeUserId])
    byAssignee[assigneeUserId] = byAssignee[assigneeUserId].map(patch)
  if (agentUserId && byAgent[agentUserId]) byAgent[agentUserId] = byAgent[agentUserId].map(patch)
  _commitMaps(root, byAssignee, byAgent)
}

// Agent roster entries assigned to a given user (default: current user),
// enriched with the assignment id/role for the UI.
export const agentsAssignedTo = (root, assigneeUserId = root?.currentUserId) => {
  if (!assigneeUserId) return []
  // Defensive: a key can exist with a non-array (undefined) value from a
  // stale optimistic write / state-clone; treat anything non-array as empty.
  const raw = root?.agentAssignmentsByAssignee?.[assigneeUserId]
  const rows = Array.isArray(raw) ? raw : []
  return rows.map((r) => {
    const a = agentBySlugUserId(root, r.agent)
    return a
      ? {
          ...a,
          _assignmentId: r.id,
          _assignmentRole: r.role,
          _assignmentRelationship: r.relationship || null
        }
      : {
          id: r.agent,
          // Both copy fields carry bundle KEYS when the row has no words of
          // its own (the KEY-IN-STATE form): YourAgentCard resolves `name` and
          // `caption` through polyglot, which returns a real name verbatim.
          name: r.agentName || 'workspace.agents.fallbackName',
          caption: 'workspace.agents.caption',
          kind: 'agent',
          userId: r.agent,
          _assignmentId: r.id,
          _assignmentRole: r.role,
          _assignmentRelationship: r.relationship || null
        }
  })
}

// People (User _ids + names) assigned to a given agent — for the canvas /
// agent detail.
export const usersAssignedToAgent = (root, agentUserId) => {
  if (!agentUserId) return []
  return root?.agentAssignmentsByAgent?.[agentUserId] || []
}
